import React, { useState, useEffect } from 'react'
import type { SafeWalletData, UndeployedSafe } from '../utils/types'
import { buildContractNetworks } from '../utils/contractNetworks'
import type { ContractNetworks } from '../utils/contractNetworks'
import { useChains } from 'wagmi'
import {
  DEFAULT_SAFE_WALLET_DATA,
  SAFE_WALLET_DATA_KEY,
} from '../utils/constants'
import { SafeWalletContext, type SafeAccountInfo } from './SafeWalletContext'

export const SafeWalletProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const chains = useChains()

  const [contractNetworks, setContractNetworks] = useState<
    ContractNetworks | undefined
  >()

  const [safeWalletData, setSafeWalletData] = useState<SafeWalletData>(() => ({
    ...DEFAULT_SAFE_WALLET_DATA,
  }))
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(SAFE_WALLET_DATA_KEY)
      if (stored) {
        try {
          setSafeWalletData(JSON.parse(stored))
        } catch {
          setSafeWalletData({
            ...DEFAULT_SAFE_WALLET_DATA,
          })
        }
      }
      setLoaded(true)
    }
  }, [])

  useEffect(() => {
    if (loaded) {
      localStorage.setItem(SAFE_WALLET_DATA_KEY, JSON.stringify(safeWalletData))
    }
  }, [safeWalletData, loaded])

  useEffect(() => {
    const chainIds = chains.map((c) => c.id)
    buildContractNetworks(chainIds)
      .then(setContractNetworks)
      .catch(() => setContractNetworks(undefined))
  }, [chains])

  const addSafe = (
    chainId: string,
    safeAddress: string,
    safeName: string,
    safeConfig?: UndeployedSafe,
    owners?: string[],
    threshold?: number
  ) => {
    setSafeWalletData((prev) => {
      const data = { ...prev.data }
      if (safeConfig) {
        if (!data.undeployedSafes[chainId]) data.undeployedSafes[chainId] = {}
        data.undeployedSafes[chainId][safeAddress] = safeConfig
      } else if (owners && threshold !== undefined) {
        // For deployed safes, store owners and threshold
        if (!data.addedSafes[chainId]) data.addedSafes[chainId] = {}
        data.addedSafes[chainId][safeAddress] = { owners, threshold }
      }

      if (!data.addressBook[chainId]) data.addressBook[chainId] = {}
      data.addressBook[chainId][safeAddress] = safeName
      return { ...prev, data }
    })
  }

  const removeSafe = (
    chainId: string,
    safeAddress: string,
    deployed = false
  ) => {
    setSafeWalletData((prev) => {
      const data = { ...prev.data }
      if (deployed) {
        if (data.addressBook[chainId]) {
          delete data.addressBook[chainId][safeAddress]
        }
      } else {
        if (data.undeployedSafes[chainId]) {
          delete data.undeployedSafes[chainId][safeAddress]
        }
      }
      return { ...prev, data }
    })
  }

  const setSafeMultiSendConfig = (
    chainId: string,
    safeAddress: string,
    multiSendAddress?: string,
    multiSendCallOnlyAddress?: string
  ) => {
    setSafeWalletData((prev) => {
      const data = { ...prev.data }
      if (!data.safeConfig) data.safeConfig = {}
      if (!data.safeConfig[chainId]) data.safeConfig[chainId] = {}

      if (!multiSendAddress && !multiSendCallOnlyAddress) {
        delete data.safeConfig[chainId][safeAddress]
        if (Object.keys(data.safeConfig[chainId]).length === 0) {
          delete data.safeConfig[chainId]
        }
      } else {
        data.safeConfig[chainId][safeAddress] = {
          multiSendAddress,
          multiSendCallOnlyAddress,
        }
      }

      return { ...prev, data }
    })
  }

  const getSafeMultiSendConfig = (chainId: string, safeAddress: string) => {
    return safeWalletData?.data?.safeConfig?.[chainId]?.[safeAddress]
  }

  const getAllSafes = (filterByOwner?: string): SafeAccountInfo[] => {
    const safes: SafeAccountInfo[] = []
    const { addressBook, undeployedSafes, addedSafes } = safeWalletData.data

    Object.entries(addressBook).forEach(([chainId, safesObj]) => {
      Object.entries(safesObj).forEach(([safeAddress, name]) => {
        const isUndeployed =
          undeployedSafes[chainId] && undeployedSafes[chainId][safeAddress]
        const deployed = !isUndeployed

        // Get owners from addedSafes or undeployedSafes
        let owners: string[] | undefined
        let threshold: number | undefined

        if (deployed && addedSafes[chainId]?.[safeAddress]) {
          owners = addedSafes[chainId][safeAddress].owners
          threshold = addedSafes[chainId][safeAddress].threshold
        } else if (!deployed && undeployedSafes[chainId]?.[safeAddress]) {
          owners =
            undeployedSafes[chainId][safeAddress].props.safeAccountConfig.owners
          threshold =
            undeployedSafes[chainId][safeAddress].props.safeAccountConfig
              .threshold
        }

        if (filterByOwner) {
          const ownerLower = filterByOwner.toLowerCase()
          const isOwner =
            owners?.some((owner) => owner.toLowerCase() === ownerLower) ?? false
          if (!isOwner) {
            return
          }
        }

        safes.push({
          chainId,
          safeAddress,
          name: String(name),
          deployed,
          owners,
          threshold,
        })
      })
    })

    return safes
  }

  return (
    <SafeWalletContext.Provider
      value={{
        safeWalletData,
        setSafeWalletData,
        contractNetworks,
        addSafe,
        removeSafe,
        setSafeMultiSendConfig,
        getSafeMultiSendConfig,
        getAllSafes,
      }}
    >
      {children}
    </SafeWalletContext.Provider>
  )
}
